'use client';

// Social Icons widget — row of social media links.

import dynamic from 'next/dynamic';
import { SOCIAL_PLATFORMS, type SocialIconsConfig, type SocialPlatform } from '@/lib/structured-builder/widget-types/social-icons';

type Props = { config: SocialIconsConfig; locale: string };

// One chunk per logo rather than a block import. Importing all ten shipped
// every platform's icon to render the handful a tenant configures; see the
// note in components/layout/Footer.tsx for why that weight matters to LCP.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ICON_MAP: Record<SocialPlatform, React.ComponentType<any>> = {
  instagram: dynamic(() =>
    import('@phosphor-icons/react/dist/csr/InstagramLogo').then((m) => m.InstagramLogo),
  ),
  x: dynamic(() =>
    import('@phosphor-icons/react/dist/csr/XLogo').then((m) => m.XLogo),
  ),
  facebook: dynamic(() =>
    import('@phosphor-icons/react/dist/csr/FacebookLogo').then((m) => m.FacebookLogo),
  ),
  tiktok: dynamic(() =>
    import('@phosphor-icons/react/dist/csr/TiktokLogo').then((m) => m.TiktokLogo),
  ),
  whatsapp: dynamic(() =>
    import('@phosphor-icons/react/dist/csr/WhatsappLogo').then((m) => m.WhatsappLogo),
  ),
  youtube: dynamic(() =>
    import('@phosphor-icons/react/dist/csr/YoutubeLogo').then((m) => m.YoutubeLogo),
  ),
  snapchat: dynamic(() =>
    import('@phosphor-icons/react/dist/csr/SnapchatLogo').then((m) => m.SnapchatLogo),
  ),
  linkedin: dynamic(() =>
    import('@phosphor-icons/react/dist/csr/LinkedinLogo').then((m) => m.LinkedinLogo),
  ),
  pinterest: dynamic(() =>
    import('@phosphor-icons/react/dist/csr/PinterestLogo').then((m) => m.PinterestLogo),
  ),
  telegram: dynamic(() =>
    import('@phosphor-icons/react/dist/csr/TelegramLogo').then((m) => m.TelegramLogo),
  ),
};

const SIZE: Record<string, { box: string; icon: string }> = {
  sm: { box: 'h-8 w-8', icon: 'h-4 w-4' },
  md: { box: 'h-10 w-10', icon: 'h-5 w-5' },
  lg: { box: 'h-12 w-12', icon: 'h-6 w-6' },
};

const ALIGN: Record<string, string> = { start: 'justify-start', center: 'justify-center', end: 'justify-end' };

export function SocialIconsWidget({ config, locale }: Props) {
  const isAr = locale === 'ar';
  const label = isAr ? config.label_ar : config.label_en;
  const sz = SIZE[config.size] ?? SIZE.md;
  const shapeCls = config.shape === 'circle' ? 'rounded-full' : 'rounded-[var(--radius-box)]';

  return (
    <div className="w-full">
      {label && <p className="text-sm font-medium text-foreground mb-3 text-center">{label}</p>}
      <div className={`flex items-center gap-3 flex-wrap ${ALIGN[config.align]}`}>
        {config.links.map((link) => {
          const Icon = ICON_MAP[link.platform];
          const platform = SOCIAL_PLATFORMS.find((p) => p.key === link.platform);
          const brandColor = platform?.brandColor ?? '#6b7280';
          const isBrand = config.color_mode === 'brand';

          const styleCls = {
            solid: `${isBrand ? '' : 'bg-foreground text-background'} ${shapeCls}`,
            outline: `border ${isBrand ? '' : 'border-border text-foreground hover:bg-subtle'} ${shapeCls}`,
            soft: `${isBrand ? '' : 'bg-foreground/10 text-foreground'} ${shapeCls}`,
            plain: '',
          }[config.style];

          const brandStyle = isBrand ? {
            solid: { backgroundColor: brandColor, color: '#fff' },
            outline: { borderColor: brandColor, color: brandColor },
            soft: { backgroundColor: brandColor + '18', color: brandColor },
            plain: { color: brandColor },
          }[config.style] : undefined;

          return (
            <a
              key={link.platform}
              href={link.url || '#'}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={platform?.label ?? link.platform}
              className={`${sz.box} inline-flex items-center justify-center transition-opacity hover:opacity-80 ${styleCls}`}
              style={brandStyle}
            >
              <Icon className={sz.icon} />
            </a>
          );
        })}
      </div>
    </div>
  );
}
